/**
 * The master-data screens (D-142), in English and French. Lingala falls back to French.
 *
 * The problem codes are the ones `engines/master.py` and `lib/master.ts` return — one key per rule, so a rule reads
 * correctly in every language without the endpoint sending a sentence.
 */

export const masterEn = {
  title: "Master data",
  hint: "Created here, once, and read by every module. OneFleet mirrors it and never invents it.",
  kind: { item: "Item", asset: "Asset", customer: "Customer", supplier: "Supplier" },
  new: {
    item: "New item",
    asset: "New asset",
    customer: "New customer",
    supplier: "New supplier",
  },
  create: "Create",
  saving: "Creating…",
  blocked: "{{n}} field(s) still stop this from being created.",
  made: "{{name}} created.",
  madeVehicle: "The vehicle record {{name}} was created with it.",
  arrives: "It reaches the add-ons on their next pull from the ERP.",
  back: "Back to where I was",
  another: "Create another",
  creditSet: "Credit limit {{amount}} set for {{company}} — the credit rule applies it from the next order.",
  creditProposed: "Credit limit {{amount}} proposed for {{company}}. Finance decides: until it is set, the credit rule does not use it.",
  isVehicle: "This asset is a vehicle",
  isVehicleHint: "The plate, odometer and fuel go on a vehicle record of their own; both are created together.",
  section: {
    what: "What it is",
    kind: "What kind",
    money: "What it cost",
    where: "Where it is",
    vehicle: "The vehicle",
    who: "Who they are",
    reaching: "Reaching them",
    terms: "Terms of sale",
  },
  choice: {
    Company: "Company",
    Individual: "Individual",
    Petrol: "Petrol",
    Diesel: "Diesel",
    "Natural Gas": "Natural gas",
    Electric: "Electric",
  },
  field: {
    item_code: "Item code",
    item_name: "Name",
    item_group: "Item group",
    stock_uom: "Unit of measure",
    brand: "Brand",
    standard_rate: "Standard rate",
    description: "Description",
    is_stock_item: "Kept in stock",
    is_fixed_asset: "Bought as a fixed asset",
    asset_category: "Asset category",
    asset_name: "Asset name",
    serial_no: "Serial number",
    company: "Company",
    purchase_date: "Purchase date",
    gross_purchase_amount: "Purchase amount",
    available_for_use_date: "In use from",
    cost_center: "Cost centre",
    location: "Location",
    custodian: "Custodian",
    license_plate: "Plate",
    make: "Make",
    model: "Model",
    fuel_type: "Fuel",
    uom: "Fuel unit",
    last_odometer: "Odometer",
    chassis_no: "Chassis number",
    color: "Colour",
    insurance_company: "Insurer",
    policy_no: "Policy number",
    end_date: "Insurance ends",
    carbon_check_date: "Technical inspection",
    customer_name: "Customer",
    customer_group: "Customer group",
    territory: "Territory",
    customer_type: "Type",
    tax_id: "Tax number",
    default_currency: "Currency",
    mobile_no: "Telephone",
    email_id: "Email",
    website: "Website",
    customer_details: "Notes",
    supplier_name: "Supplier",
    supplier_group: "Supplier group",
    supplier_type: "Type",
    country: "Country",
    supplier_details: "Notes",
    payment_terms: "Payment terms",
    default_price_list: "Price list",
    sales_person: "Sales person",
    credit_company: "Credit limit for company",
    credit_limit: "Credit limit",
    credit_limit_proposed: "Proposed credit limit",
  },
  fieldHint: {
    is_stock_item: "Spare parts and consumables are. A service is not.",
    is_fixed_asset: "A truck or a machine, which is then registered as an asset of its own.",
    custodian: "The person who can be asked where it is, when there is no location.",
    last_odometer: "What the dashboard reads today; the fleet counts from here.",
    payment_terms: "When its invoices fall due. Empty: the day they are posted.",
    default_price_list: "What its orders are priced from. Empty: the default selling list.",
    sales_person: "Put on every order at 100 %; the commission follows it.",
    credit_limit: "The amount the credit rule lends against, for the company named beside it.",
    credit_limit_proposed: "Finance sets the limit the credit rule reads; yours is stored as a proposal and shown to Finance.",
    // per kind, because the same field name means two different things on two forms
    item: {
      item_code: "What everybody will call it: on a work order, in a stock count, over the phone.",
      item_group: "A module that mirrors parts mirrors one group — start from the one its screen offered.",
    },
    asset: {
      item_code: "The item this asset was bought as; the asset category follows from it.",
      asset_name: "The plate, or what the workshop calls it — this is how it is found later.",
    },
  },
  problem: {
    required: "This is required.",
    duplicate: "Already used by {{existing}}.",
    similar_name: "{{existing}} already exists with a very similar name — check before creating a second one.",
    not_a_date: "This is not a date.",
    not_a_number: "This is not a number.",
    negative: "This cannot be negative.",
    zero: "This must be more than zero.",
    in_the_future: "This date is in the future.",
    before_purchase: "This is before the purchase date.",
    both_stock_and_fixed_asset: "An item is either kept in stock or bought as a fixed asset, not both.",
    asset_category_required: "A fixed-asset item must name an asset category.",
    bad_code: "Letters, digits and . _ / + - only.",
    bad_email: "This is not an email address.",
    unknown_value: "Not one of the values the ERP has.",
    no_insurance: "No insurer and policy number — a truck may not legally be on the road in DRC without one.",
    no_technical_inspection: "No date for the contrôle technique.",
    no_custodian_or_location: "Give a location or a custodian, so somebody can be asked where it is.",
    credit_company_required: "A credit limit is for one company: choose it.",
  },
};

export const masterFr: typeof masterEn = {
  title: "Données de référence",
  hint: "Créées ici, une seule fois, et lues par tous les modules. OneFleet les reflète et ne les invente jamais.",
  kind: { item: "Article", asset: "Immobilisation", customer: "Client", supplier: "Fournisseur" },
  new: {
    item: "Nouvel article",
    asset: "Nouvelle immobilisation",
    customer: "Nouveau client",
    supplier: "Nouveau fournisseur",
  },
  create: "Créer",
  saving: "Création…",
  blocked: "{{n}} champ(s) empêchent encore la création.",
  made: "{{name}} créé.",
  madeVehicle: "Le véhicule {{name}} a été créé avec.",
  arrives: "Il arrive dans les modules à leur prochaine synchronisation avec l'ERP.",
  back: "Retour d'où je viens",
  another: "En créer un autre",
  creditSet: "Limite de crédit de {{amount}} fixée pour {{company}} — la règle de crédit l'applique dès la prochaine commande.",
  creditProposed: "Limite de crédit de {{amount}} proposée pour {{company}}. La Finance décide : tant qu'elle ne l'a pas fixée, la règle de crédit ne l'utilise pas.",
  isVehicle: "Cette immobilisation est un véhicule",
  isVehicleHint: "La plaque, le compteur et le carburant vont sur une fiche véhicule ; les deux sont créées ensemble.",
  section: {
    what: "Ce que c'est",
    kind: "De quel type",
    money: "Ce qu'elle a coûté",
    where: "Où elle se trouve",
    vehicle: "Le véhicule",
    who: "Qui c'est",
    reaching: "Pour les joindre",
    terms: "Conditions de vente",
  },
  choice: {
    Company: "Société",
    Individual: "Particulier",
    Petrol: "Essence",
    Diesel: "Gasoil",
    "Natural Gas": "Gaz naturel",
    Electric: "Électrique",
  },
  field: {
    item_code: "Code article",
    item_name: "Nom",
    item_group: "Groupe d'articles",
    stock_uom: "Unité de mesure",
    brand: "Marque",
    standard_rate: "Prix standard",
    description: "Description",
    is_stock_item: "Tenu en stock",
    is_fixed_asset: "Acheté comme immobilisation",
    asset_category: "Catégorie d'immobilisation",
    asset_name: "Nom de l'immobilisation",
    serial_no: "Numéro de série",
    company: "Société",
    purchase_date: "Date d'achat",
    gross_purchase_amount: "Montant d'achat",
    available_for_use_date: "En service depuis",
    cost_center: "Centre de coûts",
    location: "Emplacement",
    custodian: "Responsable",
    license_plate: "Plaque",
    make: "Marque",
    model: "Modèle",
    fuel_type: "Carburant",
    uom: "Unité de carburant",
    last_odometer: "Compteur",
    chassis_no: "Numéro de châssis",
    color: "Couleur",
    insurance_company: "Assureur",
    policy_no: "Numéro de police",
    end_date: "Fin d'assurance",
    carbon_check_date: "Contrôle technique",
    customer_name: "Client",
    customer_group: "Groupe de clients",
    territory: "Territoire",
    customer_type: "Type",
    tax_id: "Numéro d'impôt",
    default_currency: "Devise",
    mobile_no: "Téléphone",
    email_id: "Courriel",
    website: "Site web",
    customer_details: "Notes",
    supplier_name: "Fournisseur",
    supplier_group: "Groupe de fournisseurs",
    supplier_type: "Type",
    country: "Pays",
    supplier_details: "Notes",
    payment_terms: "Conditions de paiement",
    default_price_list: "Liste de prix",
    sales_person: "Commercial",
    credit_company: "Limite de crédit pour la société",
    credit_limit: "Limite de crédit",
    credit_limit_proposed: "Limite de crédit proposée",
  },
  fieldHint: {
    is_stock_item: "Les pièces et consommables le sont. Un service ne l'est pas.",
    is_fixed_asset: "Un camion ou une machine, qui est ensuite enregistré comme immobilisation.",
    custodian: "La personne à qui demander où elle se trouve, à défaut d'emplacement.",
    last_odometer: "Ce que le tableau de bord affiche aujourd'hui ; la flotte compte à partir de là.",
    payment_terms: "Quand ses factures sont échues. Vide : le jour de leur comptabilisation.",
    default_price_list: "La liste qui valorise ses commandes. Vide : la liste de vente par défaut.",
    sales_person: "Mis sur chaque commande à 100 % ; la commission le suit.",
    credit_limit: "Le montant que la règle de crédit accorde, pour la société choisie à côté.",
    credit_limit_proposed: "La Finance fixe la limite que lit la règle de crédit ; la vôtre est enregistrée comme proposition et montrée à la Finance.",
    item: {
      item_code: "Ce que tout le monde utilisera : sur un ordre de travail, dans un inventaire, au téléphone.",
      item_group: "Un module qui reflète les pièces n'en reflète qu'un groupe — partez de celui que son écran a proposé.",
    },
    asset: {
      item_code: "L'article sous lequel l'immobilisation a été achetée ; la catégorie en découle.",
      asset_name: "La plaque, ou le nom que lui donne l'atelier — c'est ainsi qu'on la retrouvera.",
    },
  },
  problem: {
    required: "Ce champ est obligatoire.",
    duplicate: "Déjà utilisé par {{existing}}.",
    similar_name: "{{existing}} existe déjà sous un nom très proche — vérifiez avant d'en créer un deuxième.",
    not_a_date: "Ce n'est pas une date.",
    not_a_number: "Ce n'est pas un nombre.",
    negative: "Ne peut pas être négatif.",
    zero: "Doit être supérieur à zéro.",
    in_the_future: "Cette date est dans le futur.",
    before_purchase: "Antérieure à la date d'achat.",
    both_stock_and_fixed_asset: "Un article est tenu en stock ou acheté comme immobilisation, pas les deux.",
    asset_category_required: "Un article immobilisé doit indiquer une catégorie d'immobilisation.",
    bad_code: "Lettres, chiffres et . _ / + - uniquement.",
    bad_email: "Ce n'est pas une adresse courriel.",
    unknown_value: "Ne fait pas partie des valeurs de l'ERP.",
    no_insurance: "Ni assureur ni numéro de police — un camion ne peut légalement circuler en RDC sans assurance.",
    no_technical_inspection: "Pas de date de contrôle technique.",
    no_custodian_or_location: "Indiquez un emplacement ou un responsable, pour qu'on puisse demander où elle est.",
    credit_company_required: "Une limite de crédit vaut pour une société : choisissez-la.",
  },
};
